// The in-call chat: a plain text chat for everyone with a seat, used mostly to
// drop links. Pure logic only. The Room Durable Object stores and broadcasts;
// this file decides what is allowed, so the rules are testable without a
// socket. The chat is never part of the room snapshot (that is also served
// unauthenticated by the info route): it goes only to approved sockets.

export const CHAT_LIMITS = {
  /** Characters in one message. Long enough for a tracking URL. */
  textChars: 2000,
  /** Messages the Room keeps for a call; the oldest drop first. */
  messages: 500,
  /** How long a room can sit empty before its chat is gone: a call opened
   * again after this long starts with a clean chat (Adam, 2026-10-13: "stuff
   * i leave in the chat should not persist if i leave a meeting for 5 minutes
   * or more"). Shorter than that and he is back in the same chat. */
  quietClearsMs: 5 * 60 * 1000,
  /** Sends one participant may make per window. */
  sendsPerWindow: 12,
  sendsWindowMs: 10_000,
  /** Characters of a name kept on a message. */
  nameChars: 60,
} as const;

export interface ChatMessage {
  /** Made by the sender, so the same send arriving twice is stored once. */
  readonly id: string;
  /** The participant id of the author, which is what delete rights hang on. */
  readonly authorId: string;
  readonly authorName: string;
  readonly text: string;
  /** Epoch ms, stamped by the Room. */
  readonly at: number;
}

export interface ChatActor {
  readonly id: string;
  readonly name: string;
  readonly isHost: boolean;
}

export type ChatRefusal = "badText" | "badId" | "duplicate" | "notFound" | "forbidden";

export type ChatSendResult =
  | { readonly ok: true; readonly messages: readonly ChatMessage[]; readonly message: ChatMessage; readonly dropped: readonly string[] }
  | { readonly ok: false; readonly code: ChatRefusal };

export type ChatDeleteResult =
  | { readonly ok: true; readonly messages: readonly ChatMessage[]; readonly removed: string }
  | { readonly ok: false; readonly code: ChatRefusal };

const CONTROL = /[\u0000-\u0008\u000b\u000c\u000e-\u001f\u007f]/g;

/** Trim, drop control characters other than newline and tab, collapse runs of
 * blank lines, and refuse empty or oversized text. */
export function cleanChatText(value: unknown): string | null {
  if (typeof value !== "string") return null;
  const text = value.replace(CONTROL, "").replace(/\r\n?/g, "\n").replace(/\n{3,}/g, "\n\n").trim();
  if (!text || [...text].length > CHAT_LIMITS.textChars) return null;
  return text;
}

/** A client-made message id: short, plain characters. */
export function isChatId(value: unknown): value is string {
  return typeof value === "string" && /^[A-Za-z0-9_-]{8,64}$/.test(value);
}

/** Add one message from `actor`. Returns the new list and the ids that fell off
 * the front to stay inside the limit. */
export function applyChatSend(
  messages: readonly ChatMessage[],
  input: { readonly id: unknown; readonly text: unknown },
  actor: ChatActor,
  now: number,
): ChatSendResult {
  if (!isChatId(input.id)) return { ok: false, code: "badId" };
  const text = cleanChatText(input.text);
  if (text === null) return { ok: false, code: "badText" };
  if (messages.some((m) => m.id === input.id)) return { ok: false, code: "duplicate" };
  const message: ChatMessage = {
    id: input.id,
    authorId: actor.id,
    authorName: [...actor.name].slice(0, CHAT_LIMITS.nameChars).join(""),
    text,
    at: now,
  };
  const all = [...messages, message];
  const over = Math.max(0, all.length - CHAT_LIMITS.messages);
  return { ok: true, messages: all.slice(over), message, dropped: all.slice(0, over).map((m) => m.id) };
}

/** Delete one message: its author, or the host. */
export function applyChatDelete(messages: readonly ChatMessage[], id: unknown, actor: ChatActor): ChatDeleteResult {
  if (typeof id !== "string") return { ok: false, code: "notFound" };
  const current = messages.find((m) => m.id === id);
  if (!current) return { ok: false, code: "notFound" };
  if (!actor.isHost && current.authorId !== actor.id) return { ok: false, code: "forbidden" };
  return { ok: true, messages: messages.filter((m) => m.id !== id), removed: id };
}

// ---- Links -----------------------------------------------------------------

export type ChatSegment =
  | { readonly kind: "text"; readonly text: string }
  | { readonly kind: "link"; readonly text: string; readonly href: string };

// Characters that end a link when they trail it: sentence punctuation and the
// closing half of a bracket the link was written inside.
const TRAILING = /[.,;:!?'"’”]+$/;
const OPENERS: Readonly<Record<string, string>> = { ")": "(", "]": "[", "}": "{", ">": "<" };

// A bare address (`linkedin.com/in/someone`, `www.example.org`) is a link only
// when its ending is a real top-level domain, so "file.txt" and "e.g." stay text.
const TLDS =
  "com|net|org|edu|gov|io|co|us|uk|ca|app|dev|ai|me|ly|info|biz|tv|xyz|page|site|online|studio|design|cloud|link|ink|so|sh|gg|fm|to|cc|ws|dk|de|fr|es|it|nl|se|no|fi|ch|ie|au|nz|in|jp|cn|kr|br|mx|ar|co\\.uk|com\\.au";
const LINK = new RegExp(
  `(?:https?:\\/\\/[^\\s<>]+|www\\.[^\\s<>]+|(?<![@\\w./:-])(?:[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?\\.)+(?:${TLDS})(?![a-z0-9-])(?:[/?#][^\\s<>]*)?)`,
  "gi",
);

/** Cut trailing punctuation, and an unmatched closing bracket, off a candidate. */
function trimLink(raw: string): string {
  let url = raw;
  for (;;) {
    const before = url;
    url = url.replace(TRAILING, "");
    const last = url.slice(-1);
    const open = OPENERS[last];
    if (open && url.split(last).length > url.split(open).length) url = url.slice(0, -1);
    if (url === before) return url;
  }
}

/** The address a link opens: the text itself when it has a scheme, https
 * otherwise. Only http and https ever come out. */
export function linkHref(text: string): string | null {
  const hasScheme = /^https?:\/\//i.test(text);
  const withScheme = hasScheme ? text.replace(/^https?/i, (m) => m.toLowerCase()) : `https://${text}`;
  // Parsed by hand: this file also runs in the native app, where URL is partial.
  const m = /^https?:\/\/(?:[^\s/?#@]*@)?([^\s/?#:]+)(?::\d{1,5})?(?:[/?#]\S*)?$/.exec(withScheme);
  const host = m?.[1];
  if (!host || !/^(?:[a-z0-9](?:[a-z0-9-]*[a-z0-9])?\.)+[a-z0-9-]*[a-z]$/i.test(host)) return null;
  return withScheme;
}

/** Split a message into plain text and links, in order. */
export function chatSegments(text: string): ChatSegment[] {
  const out: ChatSegment[] = [];
  let cursor = 0;
  const push = (segment: ChatSegment): void => {
    const last = out[out.length - 1];
    if (segment.kind === "text" && last?.kind === "text") out[out.length - 1] = { kind: "text", text: last.text + segment.text };
    else out.push(segment);
  };
  for (const match of text.matchAll(LINK)) {
    const start = match.index ?? 0;
    const shown = trimLink(match[0]);
    const href = shown ? linkHref(shown) : null;
    if (!href) continue;
    if (start > cursor) push({ kind: "text", text: text.slice(cursor, start) });
    push({ kind: "link", text: shown, href });
    cursor = start + shown.length;
  }
  if (cursor < text.length) push({ kind: "text", text: text.slice(cursor) });
  return out;
}

export interface SharedLink {
  readonly href: string;
  readonly text: string;
  readonly authorName: string;
  readonly at: number;
}

/** Every link written in the chat, in order, each address once. */
export function sharedLinks(messages: readonly ChatMessage[]): SharedLink[] {
  const seen = new Set<string>();
  const out: SharedLink[] = [];
  for (const m of messages) {
    for (const s of chatSegments(m.text)) {
      if (s.kind !== "link" || seen.has(s.href)) continue;
      seen.add(s.href);
      out.push({ href: s.href, text: s.text, authorName: m.authorName, at: m.at });
    }
  }
  return out;
}
